import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { ChatViewer, isOwnMessage } from '../helpers/chatMessage';
import { PINNED_THRESHOLD_PX } from './useChatMessages';

/**
 * How many older pages the open may pull in while walking back to the first
 * unread message. The same cap the reply jump uses, for the same reason: a
 * thread with hundreds waiting lands on the oldest page this allows rather
 * than freezing the open behind a dozen round trips.
 */
const MAX_UNREAD_PAGES_WALKED = 10;

type Options = {
  conversationId: number | string | null;
  isLoading: boolean;
  messages: ConversationMessageWithStatus[];
  viewer: ChatViewer;
  /**
   * How much is waiting, as the surface opening the thread knows it - the
   * inbox row's count on admin, the badge on the merchant. The backend never
   * says WHICH message is the first unread, only how many there are, so where
   * the divider goes is counted back from the newest message.
   */
  unreadCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /** Puts the "New messages" divider just under the floating header. */
  scrollToUnreadMarker: () => boolean;
  isPinnedToBottom: () => boolean;
  markRead: () => void;
  /** Clears whatever the previous thread left behind - a draft reply, an edit. */
  onOpen?: () => void;
};

/**
 * Where the conversation puts the reader.
 *
 * Three rules now. A thread with nothing waiting opens at its newest message.
 * One with unread messages opens on a "New messages" divider instead - the
 * Telegram behaviour - so reading starts where it stopped last time and
 * continues downward. And once you are in, an incoming message must NOT yank
 * you down to it while you read history - you get a jump button instead, and
 * the thread deliberately stays unread until you take it.
 *
 * Opening still marks the whole thread read either way, exactly as Telegram
 * and WhatsApp do it: the divider is a bookmark, not a read state, and the
 * badge clears the moment you are in.
 */
export const useThreadAutoScroll = ({
  conversationId,
  isLoading,
  messages,
  viewer,
  unreadCount,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  scrollToBottom,
  scrollToUnreadMarker,
  isPinnedToBottom,
  markRead,
  onOpen,
}: Options) => {
  const [hasNewBelow, setHasNewBelow] = useState(false);

  /**
   * The message the "New messages" divider sits above.
   *
   * Computed once as the thread opens and kept for the whole visit, the way
   * Telegram keeps its marker: reading past it must not move it, or it stops
   * meaning "this is where you left off". It goes when the thread does.
   */
  const [firstUnreadId, setFirstUnreadId] = useState<number | null>(null);

  /**
   * The unread count frozen at the moment the thread came into view, and how
   * many older pages the walk below has pulled in hunting for the message
   * that carries it. Frozen because the live prop dies under us: markRead
   * fires as part of this very open, and the row answers 0 from then on.
   */
  const huntRef = useRef<{
    key: string;
    unread: number;
    pages: number;
  } | null>(null);

  /**
   * Set when the divider still needs a commit to exist before it can be
   * scrolled to - the state above renders it, the effect below positions it.
   */
  const pendingUnreadScrollRef = useRef(false);

  /**
   * A message landed on screen that we could not mark read at the time, because
   * the tab was in the background. Without this the thread stays unread until
   * something else happens to trigger a read - the badge keeps counting a
   * message that is sitting there in plain sight the moment you come back.
   */
  const missedReadRef = useRef(false);

  /* Opening a thread marks it read and drops the reader at the newest message.
     Keyed on conversationId so switching threads resets everything. */
  const openedRef = useRef<string | null>(null);
  /*
   * `onOpen` is a fresh closure on every render, so it is read through a ref
   * rather than depended on - in the deps it would re-run this effect
   * constantly, and the guard above is the only thing stopping that from
   * re-scrolling the reader to the bottom mid-read.
   */
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useLayoutEffect(() => {
    if (conversationId === null) {
      /*
       * Deselecting unmounts the scroll area (the mobile back button parks the
       * thread on `null`), so coming back to the SAME conversation is an open
       * like any other: the container it gets is brand new and sitting at the
       * top. Forgetting the thread here is what lets the guard below treat it
       * as one.
       */
      openedRef.current = null;
      huntRef.current = null;
      pendingUnreadScrollRef.current = false;
      setFirstUnreadId(null);
      return;
    }
    if (isLoading) return;
    const key = String(conversationId);
    if (openedRef.current === key) return;

    // First sight of this thread: freeze what the surface says is waiting.
    if (huntRef.current?.key !== key) {
      huntRef.current = { key, unread: unreadCount, pages: 0 };
    }
    const hunt = huntRef.current;

    /*
     * Count back from the newest message. Only what the viewer did not write
     * can be unread - their own messages never are, however new - so those are
     * skipped rather than counted. The message the count lands on is the first
     * unread, and the divider's place.
     */
    let target: ConversationMessageWithStatus | null = null;
    let counted = 0;
    if (hunt.unread > 0) {
      for (
        let i = messages.length - 1;
        i >= 0 && counted < hunt.unread;
        i -= 1
      ) {
        if (isOwnMessage(messages[i], viewer)) continue;
        counted += 1;
        target = messages[i];
      }
    }

    /*
     * Not far enough back yet: the first unread is older than anything loaded,
     * so pull one more page and let its arrival re-run this. The thread does
     * not count as opened while the walk is on - which also keeps the newest-
     * message effect below from answering a mid-walk arrival with a scroll to
     * the bottom of a thread the reader has not been placed in yet.
     */
    if (
      counted < hunt.unread &&
      hasNextPage &&
      hunt.pages < MAX_UNREAD_PAGES_WALKED
    ) {
      if (!isFetchingNextPage) {
        hunt.pages += 1;
        fetchNextPage();
      }
      return;
    }

    openedRef.current = key;
    setHasNewBelow(false);
    missedReadRef.current = false;
    onOpenRef.current?.();

    if (target) {
      // Renders the divider; the effect below lands the reader on it.
      setFirstUnreadId(target.id);
      pendingUnreadScrollRef.current = true;
    } else {
      setFirstUnreadId(null);
      scrollToBottom();
    }
    markRead();
  }, [
    conversationId,
    isLoading,
    messages,
    viewer,
    unreadCount,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollToBottom,
    markRead,
  ]);

  /**
   * Lands the reader on the divider once it exists.
   *
   * A separate pass because the state above has to render the divider before
   * anything can scroll to it. Deliberately without deps: the commit that
   * carries the divider also carries the freshly measured header, so retrying
   * every commit until the marker answers is what makes the landing
   * independent of which of the two arrives first.
   */
  useLayoutEffect(() => {
    if (!pendingUnreadScrollRef.current) return;
    if (!scrollToUnreadMarker()) return;
    pendingUnreadScrollRef.current = false;
    /* When the unread stretch runs past the fold, the jump button is offered
       straight away - the same affordance Telegram's down arrow gives from
       its divider. */
    if (!isPinnedToBottom()) setHasNewBelow(true);
  });

  /**
   * The last message in the thread, as of the previous run - and which thread
   * it belonged to.
   *
   * Keyed on that message rather than on the LENGTH of the list, which is what
   * this used to compare. A page of older history arriving at the top makes the
   * list longer too, and counting could not tell that apart from a message
   * arriving at the bottom. So scrolling up to read history re-ran everything
   * below against an unchanged newest message - and if that message happened to
   * be your own, the `mine` branch threw the reader straight back down to it.
   */
  const newestRef = useRef<{
    conversationId: string | null;
    key: string | null;
  }>({ conversationId: null, key: null });
  useEffect(() => {
    const newest = messages.length ? messages[messages.length - 1] : null;
    const key = newest ? String(newest.clientMessageId || newest.id) : null;
    const thread = conversationId === null ? null : String(conversationId);
    const previous = newestRef.current;
    newestRef.current = { conversationId: thread, key };

    if (!newest || key === null) return;
    // Nothing new at the end: an older page was prepended, or a message in
    // place was edited, deleted or ticked as seen.
    if (previous.conversationId === thread && previous.key === key) return;
    // A thread that just opened, or just changed: where the reader lands is the
    // opening effect's business, not this one's.
    if (previous.conversationId !== thread || previous.key === null) return;
    // Still walking back to the first unread: the reader has not been placed
    // yet, so there is nobody at the bottom to scroll and nothing on screen to
    // mark read. The open lands them above this message anyway.
    if (openedRef.current !== thread) return;

    // Your own message never announces itself: you know you just sent it, and
    // being told about it while reading history is nonsense.
    const mine = isOwnMessage(newest, viewer);

    if (mine || isPinnedToBottom()) {
      scrollToBottom();
      if (document.visibilityState === 'visible') markRead();
      else missedReadRef.current = true;
    } else {
      setHasNewBelow(true);
    }
  }, [
    messages,
    conversationId,
    viewer,
    isPinnedToBottom,
    scrollToBottom,
    markRead,
  ]);

  /**
   * Coming back to the thread reads what arrived while you were away.
   *
   * Both events, because they answer different questions: `visibilitychange`
   * fires when you return from another TAB, `focus` when you return from
   * another APP - a tab that was never hidden, so visibility alone would miss
   * it entirely.
   *
   * Gated on the flag above rather than firing on every focus: this is a POST
   * that also invalidates the inbox, and alt-tabbing back to an already-read
   * conversation should cost nothing. Gated on being at the bottom too - a
   * message you have scrolled away from is not one you have read, and the jump
   * button is what marks that one.
   */
  useEffect(() => {
    if (conversationId === null) return undefined;

    const readWhatIsOnScreen = () => {
      if (!missedReadRef.current) return;
      if (document.visibilityState !== 'visible') return;
      if (!isPinnedToBottom()) return;
      missedReadRef.current = false;
      markRead();
    };

    window.addEventListener('focus', readWhatIsOnScreen);
    document.addEventListener('visibilitychange', readWhatIsOnScreen);
    return () => {
      window.removeEventListener('focus', readWhatIsOnScreen);
      document.removeEventListener('visibilitychange', readWhatIsOnScreen);
    };
  }, [conversationId, isPinnedToBottom, markRead]);

  /**
   * Scrolling down to the newest message answers the jump button, so it goes
   * away on its own - leaving it up once you are already at the bottom points
   * at something you are looking at.
   */
  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasNewBelow) return;
      /*
       * Measured here rather than asked of `isPinnedToBottom`. React delivers
       * this handler from the root before the hook's own listener on the
       * element gets to update its ref, so the ref still holds the position as
       * of the previous scroll - and the button would need a second nudge to
       * go away.
       */
      const element = event.currentTarget;
      const distance =
        element.scrollHeight - element.scrollTop - element.clientHeight;
      if (distance > PINNED_THRESHOLD_PX) return;

      setHasNewBelow(false);
      if (document.visibilityState === 'visible') markRead();
    },
    [hasNewBelow, markRead]
  );

  const jumpToLatest = useCallback(() => {
    scrollToBottom('smooth');
    setHasNewBelow(false);
    markRead();
  }, [scrollToBottom, markRead]);

  return { hasNewBelow, firstUnreadId, onScroll, jumpToLatest };
};
